/**
 * Onboarding journey API client - Slice 2.2 (2026-05-20).
 *
 * Typed wrappers over GET /v1/onboarding/journey + PATCH /v1/onboarding/cards/:cardId.
 * Backed by the Slice 2.1 backend module at
 * apps/api-controller/src/modules/foundational/onboarding/.
 *
 * The auto-resolve endpoint (POST /v1/onboarding/cards/:cardId/auto-resolve)
 * is NOT exposed here - it's called internally by event listeners on the
 * backend (Vanta pattern); the IT Admin never invokes it directly.
 */

import { apiFetch } from './api';

export type OnboardingStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export type OnboardingCardId =
  | 'identity_branding'
  | 'authentication'
  | 'user_provisioning'
  | 'business_units'
  | 'connect_privacy_systems'
  | 'connect_data_sources'
  | 'detection_rules'
  | 'compliance_evidence_sinks'
  | 'notifications'
  | 'invite_dpo_team'
  | 'india_regulatory_setup'; // Frontier (dormant)

export interface OnboardingCardRow {
  id: string;
  tenantId: string;
  cardId: OnboardingCardId | string;
  status: OnboardingStatus;
  displayOrder: number;
  startedAt: string | null;
  completedAt: string | null;
  completedByUserId: string | null;
  autoResolved: boolean;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingJourney {
  cards: OnboardingCardRow[];
  totalCards: number;
  completedCards: number;
  inProgressCards: number;
  percentComplete: number;
}

/**
 * Display metadata per card. Maps card_id → user-facing label + icon hint
 * + the destination route to navigate to when the operator clicks Start.
 *
 * Phase 0 item 4 (2026-05-26) - added `route` field. Verify-before-fix
 * grep on router.tsx 2026-05-26 confirmed every route below exists.
 * Onboarding Welcome page now navigates to the actual setup page when
 * Start is clicked, in addition to PATCHing status=in_progress.
 *
 * Mode A (standalone): every route works without Core (verified - none
 *   gate on Core pairing at the route level).
 * Mode B (Core-paired): every route works the same; some pages surface
 *   richer state when Core is connected (e.g. integrations page shows
 *   Core integration card; CMP card on connect_privacy_systems).
 * Mode C (bundle): no behavioral difference; entitlement gates may hide
 *   the parent module's nav but Welcome card targets stay reachable.
 */
export const CARD_META: Record<
  string,
  { title: string; description: string; estimateMinutes: number; iconKey: string; route: string }
> = {
  identity_branding: {
    title: 'Identity & Branding',
    description: 'Upload logo, pick a primary colour, set timezone.',
    estimateMinutes: 1,
    iconKey: 'brand',
    // No dedicated branding page yet; integrations marketplace is the
    // nearest neighbour where logo/color presets live today.
    route: '/admin/integrations',
  },
  authentication: {
    title: 'Authentication (SSO)',
    description: 'Connect Okta / Azure AD / Google Workspace via SAML or OIDC.',
    estimateMinutes: 3,
    iconKey: 'shield',
    route: '/admin/auth/idp',
  },
  user_provisioning: {
    title: 'Invite your team',
    description: 'SCIM auto-sync, CSV upload, or invite users individually.',
    estimateMinutes: 2,
    iconKey: 'users',
    route: '/admin/auth/scim',
  },
  business_units: {
    title: 'Business Units',
    description: 'Define BU tree; per-BU country + regulations inherit automatically.',
    estimateMinutes: 2,
    iconKey: 'building',
    route: '/admin/business-units',
  },
  connect_privacy_systems: {
    title: 'Connect privacy systems',
    description: 'Hook up your consent-management platform (FOCTTA Core or an alternative) so consent records flow into Discovery findings and decisions.',
    estimateMinutes: 3,
    iconKey: 'lock',
    // Routes to the Core integration card (Mode B). Mode A customers
    // (no Core) can use this page to see the alternative-CMP adapter
    // catalogue (OneTrust / Securiti / TrustArc - when those adapters
    // land per architect §28). Today this page is the canonical privacy-
    // integration surface.
    route: '/admin/integrations/foctta-core',
  },
  connect_data_sources: {
    title: 'Connect your data',
    description: 'DSPM/DAM connectors - point us at databases, file shares, SaaS.',
    estimateMinutes: 3,
    iconKey: 'database',
    route: '/dspm/connectors',
  },
  detection_rules: {
    title: 'Detection rules & alerts',
    description: 'Review the rule library; pick severity thresholds + escalation.',
    estimateMinutes: 2,
    iconKey: 'siren',
    route: '/detection/rules',
  },
  compliance_evidence_sinks: {
    title: 'Compliance evidence',
    description: 'WORM bucket configuration + export schedule for auditors.',
    estimateMinutes: 2,
    iconKey: 'archive',
    route: '/admin/retention-policy',
  },
  notifications: {
    title: 'Notifications',
    description: 'Email + SMS providers (TRAI DLT for India BFSI 2-6h paging).',
    estimateMinutes: 1,
    iconKey: 'bell',
    route: '/admin/notify',
  },
  invite_dpo_team: {
    title: 'Invite DPO + team',
    description: 'Send invitations to your DPO, Legal Reviewer, and SOC.',
    estimateMinutes: 1,
    iconKey: 'user-plus',
    route: '/admin/auth/scim',
  },
  india_regulatory_setup: {
    title: 'India regulatory setup',
    description: 'DPDPA SDF inventory, SEBI CSCRF, RBI cyber framework.',
    estimateMinutes: 4,
    iconKey: 'globe',
    route: '/admin/industry-packs',
  },
};

interface JourneyResponse {
  ok: boolean;
  data: OnboardingJourney;
}

interface CardResponse {
  ok: boolean;
  data: OnboardingCardRow;
}

export async function getJourney(): Promise<OnboardingJourney> {
  const res = await apiFetch<JourneyResponse>('/v1/onboarding/journey');
  return res.data;
}

export async function updateCard(
  cardId: string,
  status: OnboardingStatus,
  payload?: Record<string, unknown>,
): Promise<OnboardingCardRow> {
  const res = await apiFetch<CardResponse>(`/v1/onboarding/cards/${encodeURIComponent(cardId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload ? { status, payload } : { status }),
  });
  return res.data;
}
